import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';

import {ActionsBuffer} from '../execution/actions_dispatcher';
import {NodeSpec, PipeId, hashMaterial} from '../execution/graph';
import {REMAINDER_WELL_VOLUME_NL} from '../execution/opentrons';
import {
  PTransformer,
  PipeContext,
  Source,
  walkMaterials,
} from '../execution/pipes';
import {
  ArrayValue,
  DataValue,
  FloatValue,
  IntValue,
  MaterialId,
  MaterialValue,
  PlateValue,
  PlatedMaterialValue,
  StringValue,
  VolumeValue,
} from '../types/data';
import {PlateState} from '../types/lab';
import {
  ETHANOL_100_PCT as MATERIAL_ETHANOL_100_PCT,
  SPRI_MAGNETIC_BEADS,
} from '../types/materials';

import {ElementSelectingTransformer} from './element_selecting_transformer';
import {makeWaterPlatedMaterial} from './sources';

const MAGNETIC_SEPARATION_OUTPUT_NL = 10_000;
const PER_WASH_VOLUME_NL = 200_000;

export const NODE = {
  id: 'node_magnetic_bead_wash',
  label: 'Magnetic bead wash',
  inputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
    {
      id: 'buffer',
      label: 'Buffer',
      type: {
        kind: 'plated_material',
        material: undefined,
      },
    },
    {
      id: 'dna_molecule_size',
      label: 'DNA molecule size',
      type: {
        kind: 'int',
        minimum: 50,
        maximum: undefined,
      },
    },
    {
      id: 'input_volume',
      label: 'Input volume',
      type: {
        kind: 'volume',
        minimumNl: 5_000,
        maximumNl: undefined,
      },
    },
    {
      id: 'suffix',
      label: 'Suffix',
      type: {kind: 'string'},
    },
    {
      id: 'wash_count',
      label: 'Washes',
      type: {
        kind: 'int',
        minimum: 1,
        maximum: undefined,
      },
    },
    {
      id: 'bead_solutions',
      label: 'Bead solutions',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
    {
      id: 'wash_material',
      label: 'Wash material',
      type: {
        kind: 'plated_material',
        material: undefined,
      },
    },
    {
      id: 'wash_strength',
      label: 'Wash strength',
      type: {
        kind: 'float',
        minimum: 0,
        maximum: 1,
      },
    },
    {
      id: 'wash_dilutant',
      label: 'Wash dilutant',
      type: {
        kind: 'plated_material',
        material: undefined,
      },
    },
    {
      id: 'output_plate',
      label: 'Plate',
      type: {
        kind: 'plate',
        contents: undefined,
      },
    },
  ],
  outputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
    {
      id: 'supernatants',
      label: 'Supernatants',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
    {
      id: 'washed_outs',
      label: 'Washes',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
  ],
  pipe: ({node, overlay}) => {
    const buffer = overlay.singleValuePadSource(
      checkExists(node.inputs.get('buffer')),
      'plated_material',
      context => makeWaterPlatedMaterial(context),
    );
    const dnaMoleculeSize = overlay.singleValuePadSource(
      checkExists(node.inputs.get('dna_molecule_size')),
      'int',
      () => ({kind: 'int', number: 100}),
    );
    const inputVolume = overlay.singleValuePadSource(
      checkExists(node.inputs.get('input_volume')),
      'volume',
      () => ({kind: 'volume', numberNl: 25_000, preferredUnit: 'ul'}),
    );
    const plate = overlay.singleValuePadSource(
      checkExists(node.inputs.get('output_plate')),
      'plate',
      context => ({
        kind: 'plate',
        id: context.lab.presetPlates.fourCelsius,
        wells: new Map(),
      }),
    );
    const suffix = overlay.singleValuePadSource(
      checkExists(node.inputs.get('suffix')),
      'string',
      () => ({kind: 'string', value: ''}),
    );
    const washCount = overlay.singleValuePadSource(
      checkExists(node.inputs.get('wash_count')),
      'int',
      () => ({kind: 'int', number: 2}),
    );
    const materials = overlay.padToSources(
      checkExists(node.inputs.get('materials')),
    );
    const beadSolutions = overlay.padToSources(
      checkExists(node.inputs.get('bead_solutions')),
    );
    const washDilutant = overlay.singleValuePadSource(
      checkExists(node.inputs.get('wash_dilutant')),
      'plated_material',
      context => makeWaterPlatedMaterial(context),
    );
    const washMaterial = overlay.singleValuePadSource(
      checkExists(node.inputs.get('wash_material')),
      'plated_material',
      context => makeEthanol100PctPlatedMaterial(context),
    );
    const washStrength = overlay.singleValuePadSource(
      checkExists(node.inputs.get('wash_strength')),
      'float',
      () => ({kind: 'float', number: 0.8, preferredDisplay: 'percentage'}),
    );

    const beadWashing = new BeadWashingTransformer(
      overlay.nextIdFor(node),
      buffer,
      dnaMoleculeSize,
      inputVolume,
      plate,
      suffix,
      washCount,
      materials as Array<
        Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
      >,
      beadSolutions as Array<
        Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
      >,
      washDilutant,
      washMaterial,
      washStrength,
    );
    overlay.setPadSource(
      checkExists(node.outputs.get('materials')),
      new ElementSelectingTransformer(
        overlay.nextIdFor(node),
        beadWashing,
        /* index= */ 0,
      ),
    );
    overlay.setPadSource(
      checkExists(node.outputs.get('supernatants')),
      new ElementSelectingTransformer(
        overlay.nextIdFor(node),
        beadWashing,
        /* index= */ 1,
      ),
    );
    overlay.setPadSource(
      checkExists(node.outputs.get('washed_outs')),
      new ElementSelectingTransformer(
        overlay.nextIdFor(node),
        beadWashing,
        /* index= */ 2,
      ),
    );
  },
} as const satisfies NodeSpec;

export class BeadWashingTransformer extends PTransformer<
  DataValue,
  ArrayValue<ArrayValue<PlatedMaterialValue>>
> {
  constructor(
    id: PipeId,
    private readonly buffer: Source<PlatedMaterialValue>,
    private readonly dnaMoleculeSize: Source<IntValue>,
    private readonly inputVolume: Source<VolumeValue>,
    private readonly plate: Source<PlateValue>,
    private readonly suffix: Source<StringValue>,
    private readonly washCount: Source<IntValue>,
    private readonly inputs: Array<
      Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
    >,
    private readonly beadSolutions: Array<
      Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
    >,
    private readonly washDilutant: Source<PlatedMaterialValue>,
    private readonly washMaterial: Source<PlatedMaterialValue>,
    private readonly washStrength: Source<FloatValue>,
  ) {
    super(id, [
      buffer,
      dnaMoleculeSize,
      inputVolume,
      plate,
      suffix,
      washCount,
      ...inputs,
      ...beadSolutions,
      washDilutant,
      washMaterial,
      washStrength,
    ]);
  }

  async collate(context: PipeContext): Promise<void> {
    const buffer = checkExists(this.values.get(this.buffer));
    const bufferPlated = checkExists(buffer.value) as PlatedMaterialValue;
    const dnaMoleculeSize = checkExists(this.values.get(this.dnaMoleculeSize));
    const dnaMoleculeSizeInt = checkExists(dnaMoleculeSize.value) as IntValue;
    dnaMoleculeSize.resolve({});
    const inputVolume = checkExists(this.values.get(this.inputVolume));
    const inputVolumeValue = checkExists(inputVolume.value) as VolumeValue;
    inputVolume.resolve({});
    const plateValue = checkExists(this.values.get(this.plate));
    const plateId = (checkExists(plateValue.value) as PlateValue).id;
    plateValue.resolve({});
    const suffixValue = checkExists(this.values.get(this.suffix));
    const suffix = (checkExists(suffixValue.value) as StringValue).value;
    suffixValue.resolve({});
    const washCount = checkExists(this.values.get(this.washCount));
    const washCountInt = checkExists(washCount.value) as IntValue;
    washCount.resolve({});
    const washDilutant = checkExists(this.values.get(this.washDilutant));
    const washDilutantPlated = checkExists(
      washDilutant.value,
    ) as PlatedMaterialValue;
    const washMaterial = checkExists(this.values.get(this.washMaterial));
    const washMaterialPlated = checkExists(
      washMaterial.value,
    ) as PlatedMaterialValue;
    const washStrength = checkExists(this.values.get(this.washStrength));
    const washStrengthFloat = (checkExists(washStrength.value) as FloatValue)
      .number;
    washStrength.resolve({});

    const beadSolutions: PlatedMaterialValue[] = [];
    const inputs: PlatedMaterialValue[] = [];
    for (const {value, source, reject} of this.values.values()) {
      if (this.beadSolutions.indexOf(source as any) >= 0) {
        walkMaterials(value, reject, v => {
          beadSolutions.push(v);
        });
      } else if (this.inputs.indexOf(source as any) >= 0) {
        walkMaterials(value, reject, v => {
          inputs.push(v);
        });
      }
    }

    const outputs: PlatedMaterialValue[] = [];
    const supernatantOutputs: PlatedMaterialValue[] = [];
    const washedOutOutputs: PlatedMaterialValue[] = [];
    {
      const plate = context.lab.plate(plateId);
      for (const plated of inputs) {
        outputs.push({
          kind: 'plated_material',
          material: beadWashMaterial(plated.material, suffix),
          plate: plate.id,
          well: plate.reserveWell().id,
        });

        supernatantOutputs.push({
          kind: 'plated_material',
          material: supernatantMaterial(plated.material),
          plate: plate.id,
          well: plate.reserveWell().id,
        });

        for (let i = 0; i < washCountInt.number; ++i) {
          washedOutOutputs.push({
            kind: 'plated_material',
            material: washedOutMaterial(plated.material, i + 1),
            plate: plate.id,
            well: plate.reserveWell().id,
          });
        }
      }
    }

    const constraints = await this.gatherConstraints(
      {
        kind: 'array',
        elements: [
          {kind: 'array', elements: outputs},
          {kind: 'array', elements: supernatantOutputs},
          {kind: 'array', elements: washedOutOutputs},
        ],
      },
      context,
    );
    const arrayConstraints = constraints.array ?? [];
    const outputsConstraints = arrayConstraints[0].array ?? [];
    const supernatantOutputsConstraints = arrayConstraints[1].array ?? [];
    const washedOutOutputsConstraints = arrayConstraints[2].array ?? [];

    let maxVolumeNl = 0;
    const used: PlatedMaterialValue[] = [];
    const usedSupernatantsWithNls: Array<[PlatedMaterialValue, number]> = [];
    const usedWashedOutsWithNls: Array<[PlatedMaterialValue, number]> = [];
    for (let i = 0; i < outputsConstraints.length; ++i) {
      const needNl = (outputsConstraints[i] ?? {}).volume?.numberNl ?? 0;
      const usedNl = needNl + (needNl > 0 ? REMAINDER_WELL_VOLUME_NL : 0);
      maxVolumeNl = Math.max(maxVolumeNl, usedNl);

      const needSupernatantNl =
        supernatantOutputsConstraints[i].volume?.numberNl ?? 0;
      const supernatant = supernatantOutputs[i];
      if (usedNl === 0 && needSupernatantNl > 0) {
        throw new Error(
          `Asking for ${supernatant.material.label} but the wash isn't being performed`,
        );
      }

      if (usedNl > 0) {
        used.push(inputs[i]);
        usedSupernatantsWithNls.push([supernatant, needSupernatantNl]);
      }

      let maxWashNl = 0;
      for (let j = 0; j < washCountInt.number; ++j) {
        const washIndex = i * washCountInt.number + j;
        const washedOutOutput = washedOutOutputs[washIndex];
        const needWashNl =
          washedOutOutputsConstraints[washIndex].volume?.numberNl ?? 0;
        maxWashNl = Math.max(maxWashNl, needWashNl);
        if (usedNl === 0 && needWashNl > 0) {
          throw new Error(
            `Asking for ${washedOutOutput.material.label} but the wash isn't being performed`,
          );
        } else if (needWashNl > PER_WASH_VOLUME_NL) {
          throw new Error(
            `Asking for more than ${PER_WASH_VOLUME_NL / 1_000}ul of ` +
              washedOutOutput.material.label,
          );
        }

        if (usedNl > 0) {
          usedWashedOutsWithNls.push([washedOutOutput, needWashNl]);
        }
      }
    }

    const magnetic = context.lab.plate(context.lab.presetPlates.magnetic);
    let beadsChanged = undefined;
    if (beadSolutions.length === used.length) {
      // TODO(april): we could move the beads over if they're on the wrong plate but meh
      for (const {value, reject} of this.beadSolutions.map(s =>
        checkExists(this.values.get(s)),
      )) {
        const valid = {allOnPlate: true};
        walkMaterials(value, reject, v => {
          if (v.plate !== magnetic.id) {
            valid.allOnPlate = false;
          }
        });

        if (!valid.allOnPlate) {
          reject('The beads must start on the magnetic plate');
        }
      }
    } else {
      beadsChanged = `Expected ${used.length} solutions, got ${beadSolutions.length}`;
      beadSolutions.length = 0;
      for (let i = 0; i < used.length; ++i) {
        const well = magnetic.reserveWell();
        beadSolutions.push({
          kind: 'plated_material',
          material: SPRI_MAGNETIC_BEADS,
          plate: magnetic.id,
          well: well.id,
        });
      }
    }

    if (maxVolumeNl === 0) {
      return;
    }

    const promises = [];
    const bufferAmount = Math.max(MAGNETIC_SEPARATION_OUTPUT_NL, maxVolumeNl);
    promises.push(
      buffer.resolve({
        volume: {
          numberNl: bufferAmount * used.length,
        },
      }),
    );
    for (const {value, resolve, reject} of this.inputs.map(s =>
      checkExists(this.values.get(s)),
    )) {
      promises.push(
        resolve(
          walkMaterials(value, reject, v =>
            used.indexOf(v) >= 0
              ? {volume: {numberNl: inputVolumeValue.numberNl}}
              : {},
          ),
        ),
      );
    }
    const beadAmount = getBeadAmount(
      dnaMoleculeSizeInt.number,
      inputVolumeValue.numberNl,
    );
    for (let i = 0; i < supernatantOutputsConstraints.length; ++i) {
      const haveNl = beadAmount + inputVolumeValue.numberNl;
      const needNl = supernatantOutputsConstraints[i].volume?.numberNl ?? 0;
      if (needNl > haveNl) {
        const label = inputs[i].material.label;
        throw new Error(
          `Asking for ${needNl / 1000}ul of supernatant from ${label} ` +
            `but only making ${haveNl / 1000}ul`,
        );
      }
    }
    for (const {resolve} of this.beadSolutions.map(s =>
      checkExists(this.values.get(s)),
    )) {
      // TODO(april): huge bug! If there are multiple bead inputs then we ask for all from each
      promises.push(
        resolve({
          array: beadSolutions.map(() => ({
            volume: {numberNl: beadAmount},
          })),
          value: beadsChanged
            ? {
                expected: {kind: 'array', elements: beadSolutions},
                reason: beadsChanged,
              }
            : undefined,
        }),
      );
    }
    const washDilutantPerWashVolumeNl =
      (1 - washStrengthFloat) * PER_WASH_VOLUME_NL;
    const washMaterialPerWashVolumeNl = washStrengthFloat * PER_WASH_VOLUME_NL;
    promises.push(
      washDilutant.resolve({
        volume: {
          numberNl:
            used.length * washCountInt.number * washDilutantPerWashVolumeNl,
        },
      }),
      washMaterial.resolve({
        volume: {
          numberNl:
            used.length * washCountInt.number * washMaterialPerWashVolumeNl,
        },
      }),
    );
    await Promise.all(promises);

    performBeadWash(
      {
        beadSolutions,
        beadVolumeNl: beadAmount,
        bufferPlated,
        bufferVolumeNl: bufferAmount,
        inputVolumeNl: inputVolumeValue.numberNl,
        magnetic,
        outputs,
        outputVolumeNl: maxVolumeNl,
        used,
        usedSupernatantsWithNls,
        usedWashedOutsWithNls,
        washCount: washCountInt.number,
        washDilutantPlated,
        washDilutantPerWashVolumeNl,
        washMaterialPlated,
        washMaterialPerWashVolumeNl,
      },
      context.dispatcher.actor(this),
      context,
    );
  }
}

function performBeadWash(
  {
    beadSolutions,
    beadVolumeNl,
    bufferPlated,
    bufferVolumeNl,
    inputVolumeNl,
    magnetic,
    outputs,
    outputVolumeNl,
    used,
    usedSupernatantsWithNls,
    usedWashedOutsWithNls,
    washCount,
    washDilutantPlated,
    washDilutantPerWashVolumeNl,
    washMaterialPlated,
    washMaterialPerWashVolumeNl,
  }: {
    beadSolutions: PlatedMaterialValue[];
    beadVolumeNl: number;
    bufferPlated: PlatedMaterialValue;
    bufferVolumeNl: number;
    inputVolumeNl: number;
    magnetic: PlateState;
    outputs: PlatedMaterialValue[];
    outputVolumeNl: number;
    used: PlatedMaterialValue[];
    usedSupernatantsWithNls: Array<[PlatedMaterialValue, number]>;
    usedWashedOutsWithNls: Array<[PlatedMaterialValue, number]>;
    washCount: number;
    washDilutantPlated: PlatedMaterialValue;
    washDilutantPerWashVolumeNl: number;
    washMaterialPlated: PlatedMaterialValue;
    washMaterialPerWashVolumeNl: number;
  },
  actor: ActionsBuffer,
  context: PipeContext,
): void {
  actor.magnetBlock(magnetic, {on: false});

  for (let i = 0; i < used.length; ++i) {
    const input = used[i];
    const bead = beadSolutions[i];
    actor.pipette(
      context.lab.plate(input.plate).well(input.well),
      context.lab.plate(bead.plate).well(bead.well),
      {mix: true, volumeNl: inputVolumeNl},
    );
  }

  // "1. Vortex the sample"
  for (let i = 0; i < used.length; ++i) {
    for (let v = 0; v < 2; ++v) {
      const bead = beadSolutions[i];
      actor.pipette(
        context.lab.plate(bead.plate).well(bead.well),
        context.lab.plate(bead.plate).well(bead.well),
        {mix: true, volumeNl: inputVolumeNl},
      );
    }
  }

  // "2. Incubate on bench for 5-15 minutes, so the DNA absorbs on the beads"
  actor.wait({minutes: 8});

  // "3. Place the sample into the magnetic rack"
  actor.magnetBlock(magnetic, {on: true});

  // "4. Wait for the solution to clear (30 seconds - 2 minutes)"
  actor.wait({minutes: 3});

  // "5. Pipette the supernatant out while keeping the tubes on the magnetic rack"
  for (let i = 0; i < used.length; ++i) {
    const bead = beadSolutions[i];
    const trash = magnetic.reserveWell();
    actor.pipette(context.lab.plate(bead.plate).well(bead.well), trash, {
      mix: false,
      sourceContaminationAllowed: true,
      volumeNl: inputVolumeNl + beadVolumeNl,
    });

    const [supernatantOutput, supernatantNeedNl] = usedSupernatantsWithNls[i];
    // TODO(april): this could be optimized by taking it straight to the output plate but it makes
    // the code more complicated and I don't care. Also the optimization isn't necessarily always
    // good because the output plate may be able to fit supernatantNeedNl but not fit the full
    // supernatant volume. Idk whatever.
    if (supernatantNeedNl > 0) {
      actor.pipette(
        trash,
        context.lab.plate(supernatantOutput.plate).well(supernatantOutput.well),
        {mix: false, volumeNl: supernatantNeedNl},
      );
    }
  }

  // "6.0. Perform two wash steps while keeping the tubes in the magnetic rack"
  const washDilutantWell = context.lab
    .plate(washDilutantPlated.plate)
    .well(washDilutantPlated.well);
  const washMaterialWell = context.lab
    .plate(washMaterialPlated.plate)
    .well(washMaterialPlated.well);
  for (let wash = 0; wash < washCount; ++wash) {
    const washSolutions = [];
    for (let i = 0; i < used.length; ++i) {
      const washMixWell = magnetic.reserveWell();
      // For 80% ethanol we need 4 tips to pull over 160ul of ethanol and 1 tip to pull over
      // 40ul of water. So it's more tip efficient to bring over the ethanol first.
      actor.pipette(washMaterialWell, washMixWell, {
        mix: false,
        volumeNl: washMaterialPerWashVolumeNl,
      });
      actor.pipette(washDilutantWell, washMixWell, {
        mix: true,
        volumeNl: washDilutantPerWashVolumeNl,
      });
      washSolutions.push(washMixWell);
    }

    // "6.1. Add 250 microliters of 80% ethanol."
    for (let i = 0; i < used.length; ++i) {
      const bead = beadSolutions[i];
      const solution = washSolutions[i];
      actor.pipette(solution, context.lab.plate(bead.plate).well(bead.well), {
        mix: true,
        sourceContaminationAllowed: true,
        volumeNl: PER_WASH_VOLUME_NL,
      });
    }

    // "6.2. Wait for 30 seconds"
    actor.evaporate(magnetic.id, {seconds: 30});

    // "6.3. Discard the supernatant"
    for (let i = 0; i < used.length; ++i) {
      const bead = beadSolutions[i];
      const solution = washSolutions[i];
      actor.pipette(context.lab.plate(bead.plate).well(bead.well), solution, {
        mix: false,
        sourceContaminationAllowed: true,
        volumeNl: PER_WASH_VOLUME_NL,
      });

      const [washedOutOutput, washedOutNeedNl] =
        usedWashedOutsWithNls[i * washCount + wash];
      if (washedOutNeedNl > 0) {
        actor.pipette(
          solution,
          context.lab.plate(washedOutOutput.plate).well(washedOutOutput.well),
          {mix: false, volumeNl: washedOutNeedNl},
        );
      }
    }
  }

  // "7. Leave test tubes with the beads open for 3-5 minutes on the bench to dry out the ethanol.
  //     Don't dry the beads completely, as the DNA yield will decline."
  actor.evaporate(magnetic.id, {minutes: 4});

  // "8.0. Elute the DNA from the beads"

  // "8.1. Remove the tubes from the magnetic rack"
  actor.magnetBlock(magnetic, {on: false});

  // "8.2. Add 20-30 microliter of water or buffer of choice to the tubes with the beads"
  for (let i = 0; i < used.length; ++i) {
    const bead = beadSolutions[i];
    actor.pipette(
      context.lab.plate(bufferPlated.plate).well(bufferPlated.well),
      context.lab.plate(bead.plate).well(bead.well),
      {mix: true, volumeNl: bufferVolumeNl},
    );
  }

  // "8.3. Vortex the tubes until all the beads are in suspension"
  for (let i = 0; i < used.length; ++i) {
    for (let v = 0; v < 2; ++v) {
      const bead = beadSolutions[i];
      actor.pipette(
        context.lab.plate(bead.plate).well(bead.well),
        context.lab.plate(bead.plate).well(bead.well),
        {mix: true, volumeNl: bufferVolumeNl},
      );
    }
  }

  // "8.4. Wait for 5-15 minutes on the bench to complete the elution."
  actor.wait({minutes: 10});

  // "9. Place the test tubes into the magnetic rack"
  actor.magnetBlock(magnetic, {on: true});

  // "10. Wait for the solution to clear (30 seconds)"
  actor.wait({minutes: 3});

  // "11. Transfer the supernatant (that contains DNA) to the fresh tubes"
  for (let i = 0; i < used.length; ++i) {
    const bead = beadSolutions[i];
    const output = outputs[i];
    actor.pipette(
      context.lab.plate(bead.plate).well(bead.well),
      context.lab.plate(output.plate).well(output.well),
      {mix: false, volumeNl: outputVolumeNl - REMAINDER_WELL_VOLUME_NL},
    );
  }

  actor.magnetBlock(magnetic, {on: false});
}

function beadWashMaterial(
  material: MaterialValue,
  suffix: string,
): MaterialValue {
  const treated: MaterialValue = {
    kind: 'material',
    id: 'uncalculated' as unknown as MaterialId,
    label: material.label + (suffix ? '.' + suffix : ''),
    amount: material.amount,
    contents: [material],
    tags: {treatment: 'bead_washed'},
  };
  treated.id = hashMaterial(treated);
  return treated;
}

function supernatantMaterial(material: MaterialValue): MaterialValue {
  const treated: MaterialValue = {
    kind: 'material',
    id: 'uncalculated' as unknown as MaterialId,
    label: material.label + '.supernatant',
    amount: material.amount,
    contents: [material],
    tags: {treatment: 'bead_wash_intermediate_supernatant'},
  };
  treated.id = hashMaterial(treated);
  return treated;
}

function washedOutMaterial(
  material: MaterialValue,
  index: number,
): MaterialValue {
  const treated: MaterialValue = {
    kind: 'material',
    id: 'uncalculated' as unknown as MaterialId,
    label: material.label + '.wash_' + index,
    amount: material.amount,
    contents: [material],
    tags: {treatment: 'bead_wash_intermediate_wash'},
  };
  treated.id = hashMaterial(treated);
  return treated;
}

function getBeadAmount(dnaMoleculeSize: number, volumeNl: number): number {
  let chosenRatio = 1.5;
  for (const [minimum, ratio] of [
    [100, 1.5],
    [200, 1.0],
    [300, 0.8],
    [400, 0.7],
    [600, 0.6],
    [700, 0.5],
    [1300, 0.4],
  ]) {
    if (dnaMoleculeSize < minimum) {
      break;
    }
    chosenRatio = ratio;
  }
  return chosenRatio * volumeNl;
}

function makeEthanol100PctPlatedMaterial(
  context: PipeContext,
): PlatedMaterialValue {
  const plate = context.lab.presetPlates.eppendorfs;
  const well = context.lab
    .plate(plate)
    .getSharedWell(MATERIAL_ETHANOL_100_PCT.id);
  return {
    kind: 'plated_material',
    material: MATERIAL_ETHANOL_100_PCT,
    plate: plate,
    well: well.id,
  };
}
